import type { Feature } from 'ol';
import type { Geometry } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import { transformExtent } from 'ol/proj';
import VectorSource from 'ol/source/Vector';

import type {
  DrawToolType,
  GeoJsonStylePropertiesDTO,
} from '@nora-monorepo/nora-api-client';
import {
  BBox,
  isNonEmptyString,
  NoraSupportedCoordinateFormats,
} from '@nora-monorepo/shared';

import type { DrawTool, DrawToolOptions } from '#nora-ui/map/drawtools/types';
import { DrawToolSaveMode } from '#nora-ui/map/drawtools/types';
import type { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';

interface FeatureEvent extends Event {
  detail: Feature<Geometry>;
}

export abstract class BaseDrawTool implements DrawTool {
  private eventTarget = new EventTarget();

  /**
   * The draw tool name being displayed in the UI.
   */
  drawToolName: string;

  /**
   * The feature name
   */
  featureName?: string;

  /**
   * The feature name
   */
  featureNameVisible?: boolean = true;

  abstract options: DrawToolOptions;
  abstract drawToolType: DrawToolType;

  protected drawToolManager: DrawToolManager;

  protected editingVectorSource: VectorSource = new VectorSource();
  protected editingVectorLayer: VectorLayer<VectorSource> = new VectorLayer({
    source: this.editingVectorSource,
  });

  protected feature?: Feature<Geometry> | null;

  // Store the featureId in case we're editing a feature
  featureId?: string;
  sourceDrawLayerId?: string;
  isEdited = false;

  protected saveMode: DrawToolSaveMode = DrawToolSaveMode.Save;

  protected constructor(drawToolManager: DrawToolManager) {
    this.drawToolManager = drawToolManager;
    this.drawToolName = 'BaseDrawTool';

    this.activate = this.activate.bind(this);
    this.addOrUpdateFeature = this.addOrUpdateFeature.bind(this);
    this.deactivate = this.deactivate.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.getDefaultProperties = this.getDefaultProperties.bind(this);
    this.onStylingUpdated = this.onStylingUpdated.bind(this);
    this.save = this.save.bind(this);
  }

  markDirty(): void {
    this.isEdited = true;
  }

  resetDirty(): void {
    this.isEdited = false;
  }

  getFeature(): Feature<Geometry> | null {
    return this.feature ?? null;
  }

  /**
   * Implemented in each draw tool, used to activate the draw tool when you want to use it on the map.
   */
  abstract activate(): void;

  /**
   * Implemented in each draw tool, used to deactivate the draw tool when you no longer want to use it on the map.
   */
  abstract deactivate(): void;

  getDefaultProperties(): Partial<GeoJsonStylePropertiesDTO> {
    return {
      ...this.drawToolManager.drawStyleOptions,
      name: this.featureName ?? '',
      nameVisible: this.featureNameVisible ?? false,
      drawToolType: this.drawToolType,
    };
  }

  /**
   * Adds a new feature to the vector source or updates it if it already exists.
   *
   * @param {VectorSource} vectorSource
   * @param {Feature} newFeature - The feature to add or update.
   */
  addOrUpdateFeature(
    vectorSource: VectorSource,
    newFeature: Feature<Geometry>,
  ): void {
    const featureId = newFeature.getId();
    if (featureId === undefined) {
      return;
    }

    const existingFeature = vectorSource.getFeatureById(featureId);
    if (existingFeature) {
      vectorSource.removeFeature(existingFeature);
    }

    vectorSource.addFeature(newFeature);
  }

  /**
   * Can be overridden in each draw tool, used to save the feature to the map.
   */
  async save(_feature?: Feature<Geometry> | null): Promise<void> {
    const featureToSave = _feature || this.feature;
    if (!featureToSave) {
      console.error('Failed to save feature, no feature provided.');
      return;
    }

    if (this.saveMode === DrawToolSaveMode.Emit) {
      await this.emitSave(featureToSave);
    }
    else if (this.saveMode === DrawToolSaveMode.Save) {
      await this.saveFeature(featureToSave);
    }
  }

  /**
   * Saves a feature to the draw tool manager
   * @param feature
   */
  async saveFeature(feature: Feature<Geometry>): Promise<Feature<Geometry>> {
    if (isNonEmptyString(this.sourceDrawLayerId)) {
      feature.set('drawLayerId', this.sourceDrawLayerId);
    }

    const savedFeature = await this.drawToolManager.saveFeature(feature);

    this.addOrUpdateFeature(this.drawToolManager.vectorSource, savedFeature);

    return savedFeature;
  }

  async emitSave(feature: Feature<Geometry>): Promise<void> {
    this.emit('save', feature);
  }

  async on(
    event: string,
    callback: (event: Feature<Geometry>) => void,
  ): Promise<void> {
    this.eventTarget.addEventListener(event, (evt: Event) => {
      const featureEvent = evt as FeatureEvent;
      callback(featureEvent.detail);
    });
  }

  async off(event: string, callback: (event: Event) => void): Promise<void> {
    this.eventTarget.removeEventListener(event, callback);
  }

  emit(event: string, detail: unknown): void {
    this.eventTarget.dispatchEvent(new CustomEvent(event, { detail }));
  }

  /**
   * Dismisses the draw tool.
   */
  dismiss(): void {
    this.drawToolManager.map.drawToolUI.setDrawToolByType(null);
  }

  /**
   * Sets the feature name for the draw tool.
   */
  setFeatureName(name: string): void {
    this.featureName = name;
    this.onStylingUpdated();
  }

  /**
   * Sets the feature name visibility for the draw tool.
   */
  setFeatureNameVisible(visible: boolean): void {
    this.featureNameVisible = visible;
    this.onStylingUpdated();
  }

  /**
   * Returns default label placeholder text for tool.
   * Override in specific draw tools when placeholder needed.
   */
  getLabelPlaceholder(): string {
    return '';
  }

  /**
   * Show the draw tool styling options for the draw tool.
   */
  showStyles(): boolean {
    return true;
  }

  /**
   * Shows the fill color selection in the draw tool style options.
   */
  showFillColor(): boolean {
    return false;
  }

  /**
   * Shows the fill style selection (no fill/fill).
   */
  showFillStyle(): boolean {
    return true;
  }

  /**
   * Shows the line/stroke color selection in the draw tool style options.
   */
  showLineColor(): boolean {
    return true;
  }

  /**
   * Shows the line/stroke opacity selection in the draw tool style options.
   */
  showLineOpacity(): boolean {
    return true;
  }

  /**
   * Shows the line style (no-line/line)
   */
  showLineStyle(): boolean {
    return true;
  }

  /**
   * Shows the line/stroke width selection in the draw tool style options.
   */
  showLineWidth(): boolean {
    return true;
  }

  showFillTab(): boolean {
    return true;
  }

  showLineTab(): boolean {
    return true;
  }

  /**
   * Shows the fill tab in the draw tool style options.
   */
  showInfoTab(): boolean {
    return true;
  }

  /**
   * Is called when the style for a feature is updated.
   */
  onStylingUpdated(): void {

  }

  /**
   * Sets the save mode for the draw tool. By default the tool saves the feature into the draw tool manager,
   *  but it can also emit the feature so it can be controlled elsewhere.
   * @param mode
   */
  setSaveMode(mode: DrawToolSaveMode): void {
    this.saveMode = mode;
  }

  /**
   * Gets the bounding box buffer based on the stroke width of the feature.
   * This is used to expand the bounding box when calculating the feature's extent.
   * @return The bounding box buffer in map units.
   */
  getBoundingBoxBuffer(feature = this.feature): number {
    const strokeWidth = (feature?.get('lineWidth') as number | undefined) ?? 0;
    const resolution = this.drawToolManager.map.getOlMapOrThrow().getView().getResolution() ?? 1;

    return (strokeWidth / 2) * resolution;
  }

  /**
   * Gets the bounding box of the current feature.
   * @return An array representing the bounding box [minX, minY, maxX, maxY] or null if no feature exists.
   */
  getFeatureBoundingBox(feature = this.feature): BBox | null {
    if (!feature) {
      return null;
    }
    const geometry = feature.getGeometry();
    if (!geometry) {
      return null;
    }

    const boundingBoxBuffer = this.getBoundingBoxBuffer();
    const extent = geometry.getExtent();
    const [minX, minY, maxX, maxY] = extent;
    if (
      minX === undefined
      || minY === undefined
      || maxX === undefined
      || maxY === undefined
    ) {
      return null;
    }
    const adjustedExtent: [number, number, number, number] = [
      minX - boundingBoxBuffer,
      minY - boundingBoxBuffer,
      maxX + boundingBoxBuffer,
      maxY + boundingBoxBuffer,
    ];

    const geoExtent = transformExtent(
      adjustedExtent,
      NoraSupportedCoordinateFormats.WEB_MERCATOR,
      NoraSupportedCoordinateFormats.WGS84,
    );
    return BBox.fromBboxArray(geoExtent);
  }
}
